import { useState, useEffect, useRef, useCallback } from 'react';
import DoramaCard from '../components/DoramaCard';
import SkeletonCard from '../components/SkeletonCard';
import { useGetMaterialsQuery } from '../services/materialsApi';
import styles from '../css/Catalog.module.css';

const Lakorns = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const dramasRef = useRef([]);
  const lastAppendedPageRef = useRef(0);
  const sentinelRef = useRef(null);

  const { data, isLoading, isFetching } = useGetMaterialsQuery({
    country: 'Таиланд',
    sort_by: 'imdb_rating',
    sort_order: 'desc',
    per_page: 24,
    page,
    ...(search.trim() && { search: search.trim() }),
  });

  if (data?.data && !isFetching) {
    if (page === 1 && lastAppendedPageRef.current !== 1) {
      dramasRef.current = data.data;
      lastAppendedPageRef.current = 1;
    } else if (page > 1 && lastAppendedPageRef.current < page) {
      dramasRef.current = [...dramasRef.current, ...data.data];
      lastAppendedPageRef.current = page;
    }
  }

  const allDramas = dramasRef.current;
  const totalCount = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.total_pages ?? 1;

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching && page < totalPages) {
          setPage(p => p + 1);
        }
      },
      { rootMargin: '0px 0px -300px 0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [isFetching, page, totalPages]);

  const resetPage = useCallback(() => {
    dramasRef.current = [];
    lastAppendedPageRef.current = 0;
    setPage(1);
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetPage();
  };

  return (
    <div className={styles.catalogPage}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Лакорны</h1>
          <p className={styles.subtitle}>Тайские дорамы</p>
        </div>

        <div className={styles.searchBar}>
          <svg className={styles.searchIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Поиск по названию..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        <div className={styles.resultsInfo}>
          <p>Найдено: <span className={styles.count}>{totalCount}</span> лакорнов</p>
        </div>

        {(isLoading || isFetching) && allDramas.length === 0 ? (
          <div className={styles.dramasGrid}>
            {Array.from({ length: 24 }, (_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : (
          <div className={styles.dramasGrid}>
            {allDramas.map(drama => (
              <DoramaCard
                key={drama.id}
                id={drama.id}
                slug={drama.slug}
                slug_id={drama.slug_id}
                image={`https://doramahype.ru/uploads/posters/${drama.poster_url}`}
                title={drama.title}
                rating={drama.imdb_rating}
                year={drama.year}
                genres={drama.genres ?? []}
                status={drama.drama_status}
              />
            ))}
          </div>
        )}

        {!isLoading && allDramas.length === 0 && (
          <div className={styles.noResults}>
            <p>По вашему запросу ничего не найдено</p>
            <p className={styles.noResultsSubtext}>Попробуйте изменить поисковый запрос</p>
          </div>
        )}

        <div ref={sentinelRef} className={styles.sentinel} />

        {isFetching && page > 1 && (
          <div className={styles.loadingMore}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Lakorns;
